<template>
  <MglMap
    v-if="!vesselPositionDataLoading"
    ref="map"
    :access-token="accessToken"
    :map-style="style"
    :max-zoom="10"
    :min-zoom="1"
    class="map-style"
    @load="onMapLoaded"
  >
    <!-- INITIAL POSITION -->
    <MglMarker
      v-if="safeInitialVesselCoordinates"
      :coordinates="safeInitialVesselCoordinates"
    >
      <div
        slot="marker"
        style="
          background-color: white;
          border-radius: 50%;
          height: fit-content;
          width: fit-content;
          padding: 5px;
        "
      >
        <v-icon
          slot="marker"
          color="colors.red.darken1"
          size="20"
        >
          mdi-flag
        </v-icon>
      </div>
    </MglMarker>

    <!-- ROUTE LINE -->
    <MglGeojsonLayer
      v-if="safeVoyageCoordinatesGeoJson && safeVoyageCoordinateLineLayer"
      :source-id="safeVoyageCoordinatesGeoJson.data.id"
      :source="safeVoyageCoordinatesGeoJson"
      :layer-id="safeVoyageCoordinateLineLayer.id"
      :layer="safeVoyageCoordinateLineLayer"
    />

    <!-- ROUTE POINTS -->
    <MglGeojsonLayer
      v-if="safeVoyageCoordinatesGeoJson && safeVoyageCoordinatePointLayer"
      :source-id="safeVoyageCoordinatesGeoJson.data.id"
      :source="safeVoyageCoordinatesGeoJson"
      :layer-id="safeVoyageCoordinatePointLayer.id"
      :layer="safeVoyageCoordinatePointLayer"
    />

    <!-- LATEST POSITION -->
    <MglMarker
      v-if="safeLatestVesselCoordinates"
      :coordinates="safeLatestVesselCoordinates"
    >
      <div
        slot="marker"
        style="
          background-color: white;
          border-radius: 50%;
          height: fit-content;
          width: fit-content;
          padding: 5px;
        "
      >
        <v-icon
          slot="marker"
          color="colors.red.darken1"
          size="20"
        >
          mdi-ferry
        </v-icon>
      </div>
    </MglMarker>

    <!-- LIVE POSITION -->
    <MglMarker
      v-if="safeCurrentVesselPosition"
      :coordinates="safeCurrentVesselPosition"
    >
      <v-col slot="marker">
        <v-row>
          <v-btn
            fab
            x-small
            color="light-green"
            class="ma-auto"
          >
            <v-icon color="#FFFFFF">
              mdi-ferry
            </v-icon>
          </v-btn>
        </v-row>
        <v-row>
          <div class="mapTitle ma-auto">
            Live Vessel Position
          </div>
        </v-row>
      </v-col>
    </MglMarker>
  </MglMap>

  <div
    v-else
    class="map-loading-container"
  >
    <span style="color: var(--color-text-muted)">Loading map data...</span>
    <v-progress-circular
      indeterminate
      color="primary"
      size="48"
    />
  </div>
</template>

<script>
import { MglMap, MglMarker, MglGeojsonLayer } from "vue-mapbox";
import Mapbox from "mapbox-gl";
import { mapGetters, mapState } from "vuex";

export default {
  name: "VoyageMap",
  components: {
    MglMap,
    MglMarker,
    MglGeojsonLayer,
  },

  data: function () {
    return {
      accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      style: process.env.VUE_APP_MAPBOX_STYLE,
      flyTo: null,
      hoverBound: false,
    };
  },

  computed: {
    ...mapGetters(["getVesselsData"]),

    ...mapState("vesselInformation/vesselPositionModule", [
      "vesselPositionDataLoading",
    ]),

    ...mapGetters("vesselInformation/vesselPositionModule", [
      "initialVesselCoordinates",
      "latestVesselCoordinates",
      "voyageCoordinatesGeoJson",
      "voyageCoordinateLineLayer",
      "voyageCoordinatePointLayer",
    ]),

    // -------- SAFE COORDS (NO SWAP) --------
    safeInitialVesselCoordinates() {
      return this.toValidCoordArray(this.initialVesselCoordinates);
    },

    safeLatestVesselCoordinates() {
      return this.toValidCoordArray(this.latestVesselCoordinates);
    },

    safeCurrentVesselPosition() {
      // If getVesselsData isn't ready, don't render marker (prevents crash)
      if (!this.getVesselsData || !Array.isArray(this.getVesselsData)) return null;

      const currentVessel = this.$route && this.$route.params ? this.$route.params.vessel : null;
      if (!currentVessel) return null;

      let found = null;
      for (let i = 0; i < this.getVesselsData.length; i++) {
        const item = this.getVesselsData[i];
        if (item && item.Vessel === currentVessel) {
          found = item;
          break;
        }
      }

      if (!found || !found.coordinates) return null;

      return this.toValidCoordArray(found.coordinates);
    },

    // -------- SAFE GEOJSON + LAYERS --------
    safeVoyageCoordinatesGeoJson() {
      const g = this.voyageCoordinatesGeoJson;

      // must exist and have data.id (what your template uses)
      if (!g || !g.data || !g.data.id) return null;

      // If features missing/empty, still allow rendering (Mapbox can render empty source)
      // but to be safe, require FeatureCollection-ish structure
      if (g.data.type && g.data.type !== "FeatureCollection") return null;
      if (g.data.features && !Array.isArray(g.data.features)) return null;

      return g;
    },

    safeVoyageCoordinateLineLayer() {
      const l = this.voyageCoordinateLineLayer;
      if (!l || !l.id) return null;
      return l;
    },

    safeVoyageCoordinatePointLayer() {
      const l = this.voyageCoordinatePointLayer;
      if (!l || !l.id) return null;
      return l;
    },
  },

  created() {
    this.mapbox = Mapbox;
  },

  methods: {
    // NO SWAP. Only validates + coerces to numbers.
    toValidCoordArray(coords) {
      if (!coords || !Array.isArray(coords) || coords.length !== 2) return null;

      const a = Number(coords[0]);
      const b = Number(coords[1]);

      if (!isFinite(a) || !isFinite(b)) return null;

      // keep as-is (whatever your backend provides)
      return [a, b];
    },

    onMapLoaded(event) {
      // flyTo can exist even when coords are null (we guard below)
      this.flyTo = event && event.component && event.component.actions
        ? event.component.actions.flyTo
        : null;

      // Keep your exact behavior but prevent crashes
      let center = this.safeInitialVesselCoordinates;

      if (!center) center = this.safeCurrentVesselPosition;
      if (!center) center = this.safeLatestVesselCoordinates;

      if (this.flyTo && center) {
        this.flyTo({
          center: center,
          zoom: 1,
          speed: 8,
        });
      }

      this.hoverBound = false;
      this.bindHoverHandlersWhenReady(event.map);
    },

    bindHoverHandlersWhenReady(map) {
      if (!map) return;

      const self = this;
      const layerId = "voyageCoordinatesPoint";

      function tryBind() {
        if (self.hoverBound) return;

        try {
          if (!map.getLayer || !map.getLayer(layerId)) {
            // layer not ready yet; try again shortly
            setTimeout(tryBind, 200);
            return;
          }

          map.on("mouseenter", layerId, self.onMouseEnter);
          map.on("mouseleave", layerId, self.onMouseLeave);
          self.hoverBound = true;
        } catch (e) {
          // If mapbox throws because layer isn't there yet, retry
          setTimeout(tryBind, 200);
        }
      }

      tryBind();
    },

    onMouseEnter(event) {
      try {
        if (!event || !event.features || !event.features[0]) return;

        new this.mapbox.Popup({
          maxWidth: "500px",
          anchor: "top",
          closeButton: false,
          closeOnClick: false,
          className: "custom-popup",
          offset: 10,
        })
          .setLngLat(event.features[0].geometry.coordinates)
          .setHTML(
            "<div class=\"vessel-popup-content\">" +
              (event.features[0].properties ? event.features[0].properties.description : "") +
            "</div>"
          )
          .addTo(this.$refs.map.map);
      } catch (e) {
        // swallow (no crash)
      }
    },

    onMouseLeave() {
      try {
        const popups = document.getElementsByClassName("mapboxgl-popup");
        if (popups && popups[0]) popups[0].remove();
      } catch (e) {
        // swallow
      }
    },
  },
};
</script>

<style>
.map-loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--color-surface);
  height: 100%;
  gap: 20px;
  border: 2px solid lightblue;
}

.map-style {
  border: 2px solid lightblue;
}
.custom-popup {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 0;
}

.custom-popup .mapboxgl-popup-content {
  border-radius: 8px !important;
  padding: 5px 15px !important;
}

.popup-title {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #1976d2 !important;
  margin-bottom: 10px !important;
  padding-bottom: 8px !important;
  border-bottom: 2px solid #1976d2 !important;
}

.custom-popup .mapboxgl-popup-close-button {
  font-size: 20px !important;
  color: #666 !important;
  padding: 5px 10px !important;
  right: 0 !important;
  top: 0 !important;
}

.custom-popup .mapboxgl-popup-close-button:hover {
  background-color: transparent !important;
  color: #000 !important;
}

.vessel-popup-content {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif !important;
  line-height: 1.5 !important;
  color: #333 !important;
}

.popup-item {
  display: flex !important;
  align-items: center !important;
  padding: 8px 0 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.popup-item:last-child {
  border-bottom: none !important;
}

.popup-item i {
  font-size: 20px !important;
  color: #1976d2 !important;
  margin-right: 12px !important;
  width: 24px !important;
  text-align: center !important;
}

.popup-item-content {
  display: flex !important;
  flex-direction: column !important;
}

.popup-label {
  font-size: 12px !important;
  color: #666 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

.popup-value {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #333 !important;
  margin-top: 2px !important;
}

.custom-popup .mapboxgl-popup-tip {
  border-top-color: rgba(255, 255, 255, 0.95) !important;
}
</style>
